import { X, Settings, Star } from 'lucide-react';
import { Button } from '@/components/ui';
import { sgmDefaultTemplate } from '@/data/templates';

interface PhaseTemplate {
  name: string;
  phase: string;
  tasks: TaskTemplate[];
}

interface TaskTemplate {
  name: string;
  duration: number | null;
  minDuration?: number;
  startNode?: string;
  endNode?: string;
  predecessor?: string;
  subTask?: string;
  description?: string;
  offsetWeeks?: number;
  startNodeOffset?: number;
  endNodeOffset?: number;
  overlapWeeks?: number;
  functionalTask?: string;
  reviewTask?: boolean;
}

interface ComponentTimingTemplate {
  id: string;
  oem: string;
  product: string;
  description: string;
  phases: PhaseTemplate[];
  created_at: string;
  updated_at: string;
}

interface ComponentTimingTemplateViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: ComponentTimingTemplate | null;
}

export function ComponentTimingTemplateViewModal({
  isOpen,
  onClose,
  template
}: ComponentTimingTemplateViewModalProps) {
  if (!isOpen || !template) return null;

  const phases = template.phases || sgmDefaultTemplate;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center text-blue-600">
            <Settings className="w-6 h-6 mr-2" />
            <div>
              <h3 className="text-lg font-bold">{template.oem} - {template.product}</h3>
              <p className="text-xs text-gray-500">{template.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">OEM:</span>
                <span className="ml-2 font-medium text-gray-800">{template.oem}</span>
              </div>
              <div>
                <span className="text-gray-500">Product:</span>
                <span className="ml-2 font-medium text-gray-800">{template.product}</span>
              </div>
              <div>
                <span className="text-gray-500">Template:</span>
                <span className="ml-2 font-medium text-gray-800">Component Timing Platform</span>
              </div>
            </div>
          </div>

          {phases.map((phase) => (
            <div key={phase.phase} className="mb-8">
              <h4 className="text-md font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200 bg-gray-50 px-3 py-2 rounded">
                {phase.name}
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[6%]">功能任务</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[4%]">评审</th>
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[10%]">一级任务</th>
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[12%]">二级子任务</th>
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[7%]">开始节点</th>
                      <th className="text-center pl-0 pr-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[5%]">Offset</th>
                      <th className="text-left px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[7%]">完成节点</th>
                      <th className="text-center pl-0 pr-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[5%]">Offset</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[5%]">最短工期</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[5%]">最长工期</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-700 border-b-2 border-gray-300 w-[5%]">同步工期</th>
                    </tr>
                  </thead>
                  <tbody>
                    {phase.tasks.map((task, idx) => {
                      const functionalNodes = ['项目立项', '数据发布', '首次造车', '可销售车'];
                      const isFunctionalStart = task.startNode && functionalNodes.includes(task.startNode);
                      const isFunctionalEnd = task.endNode && functionalNodes.includes(task.endNode);

                      const formatOffset = (offset?: number) => {
                        if (offset === undefined || offset === null) return '-';
                        return offset > 0 ? `+${offset}周` : `${offset}周`;
                      };

                      const startOffset = task.startNodeOffset !== undefined && task.startNodeOffset !== null ? task.startNodeOffset : undefined;
                      const endOffset = task.endNodeOffset !== undefined && task.endNodeOffset !== null ? task.endNodeOffset : undefined;

                      return (
                        <tr key={idx} className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
                          <td className="px-3 py-3 text-center">
                            {task.functionalTask && task.functionalTask !== '-' ? (
                              <div className="flex items-center justify-center" title={`功能任务: ${task.functionalTask}`}>
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                <span className="ml-1 text-xs text-yellow-700">{task.functionalTask}</span>
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {task.reviewTask ? (
                              <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                是
                              </span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 font-medium text-gray-800 align-top">
                            {idx === 0 || task.subTask !== phase.tasks[idx - 1].subTask ? task.subTask : ''}
                          </td>
                          <td className="px-3 py-3 font-medium text-gray-900">{task.name}</td>
                          <td className="px-3 py-3 text-gray-700">
                            {task.startNode ? (
                              isFunctionalStart ? (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                  {task.startNode}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                  {task.startNode}完成
                                </span>
                              )
                            ) : task.predecessor ? (
                              <span className="text-gray-500 text-xs">前序完成</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="pl-0 pr-3 py-3 text-center">
                            <span className="font-medium text-gray-700">{formatOffset(startOffset)}</span>
                          </td>
                          <td className="px-3 py-3 text-gray-700">
                            {task.endNode ? (
                              isFunctionalEnd ? (
                                <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                                  {task.endNode}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                                  {task.endNode}完成
                                </span>
                              )
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="pl-0 pr-3 py-3 text-center">
                            <span className="font-medium text-gray-700">{formatOffset(endOffset)}</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {task.minDuration ? (
                              <span className="font-medium text-blue-600">{Math.round(task.minDuration / 7)}周</span>
                            ) : task.minDuration === null ? (
                              <span className="text-orange-500 font-medium">N/A</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {task.duration ? (
                              <span className="font-medium text-gray-800">{Math.round(task.duration / 7)}周</span>
                            ) : task.duration === null ? (
                              <span className="text-orange-500 font-medium">N/A</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {task.overlapWeeks ? (
                              <span className="font-medium text-purple-600">{task.overlapWeeks}周</span>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="mt-6 p-4 bg-gray-50 rounded-lg text-xs text-gray-600">
            <h5 className="font-semibold mb-2">图例说明:</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="flex items-center">
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 mr-1" />
                <span className="text-yellow-700 font-medium mr-1">产品T0</span>
                <span>功能任务</span>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded mr-2">是</span>
                <span>评审任务</span>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded mr-2">数据发布</span>
                <span>功能节点(开始)</span>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 rounded mr-2">首次造车</span>
                <span>功能节点(完成)</span>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded mr-2">任务完成</span>
                <span>任务引用(开始)</span>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 rounded mr-2">任务完成</span>
                <span>任务引用(完成)</span>
              </div>
              <div className="flex items-center">
                <span className="text-orange-500 font-medium mr-2">N/A</span>
                <span>动态计算工期</span>
              </div>
              <div className="flex items-center">
                <span className="text-purple-600 font-medium mr-2">N周</span>
                <span>同步工期(overlap)</span>
              </div>
              <div className="flex items-center">
                <span className="font-medium text-gray-700 mr-2">-4周/+2周</span>
                <span>Offset偏移量</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end p-4 border-t bg-gray-50">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
